import React from 'react';

import classNames from 'classnames';

import Alert from 'CommonComponents/Alert';
import Badge from 'CommonComponents/Badge';

import LockIcon from 'Assets/icons/lock-closed.svg';

export const EVENT_CURRENCY = 'event';

/**
 * The page's FX options from the mount element: the anchor, every currency in
 * the organisation's table and whether it has a rate, and the FX page link
 * (only for viewers who may manage FX).
 */
export function readFxOptions(rootEl) {
    try {
        return JSON.parse(rootEl.getAttribute('data-fx') || 'null') || { anchor: null, options: [], fx_url: null };
    } catch (e) {
        return { anchor: null, options: [], fx_url: null };
    }
}

/** A rate against the anchor, to the four places it is shown at. */
export function formatRate(rate) {
    return rate === null || rate === undefined ? '' : Number(rate).toFixed(4);
}

/**
 * "Show in": each project's own currency (the report as it always was), the
 * anchor, or any other currency in the FX table. A currency with no rate is
 * listed but cannot be chosen; with no anchor only Event currency is offered.
 */
export default function ShowInSelect({ fxOptions, value, onChange }) {
    const anchor = fxOptions?.anchor || null;
    const options = anchor ? fxOptions.options || [] : [];

    return (
        <label className="tw-block tw-mb-0">
            <span className="tw-block tw-text-sm tw-text-blue-75 tw-font-medium tw-mb-2">{$t('Show in')}</span>
            <select
                className="tw-min-w-[180px] tw-border-2 tw-border-grey tw-rounded-lg tw-text-sm tw-text-charcoal tw-px-3 tw-py-1.5 tw-bg-white"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            >
                <option value={EVENT_CURRENCY}>{$t('Event currency')}</option>
                {anchor && <option value={anchor}>{$t('{{currency}} (anchor)', { currency: anchor })}</option>}
                {options.map((option) => (
                    <option key={option.code} value={option.code} disabled={!option.has_rate}>
                        {option.has_rate ? option.code : $t('{{currency}} (no rate)', { currency: option.code })}
                    </option>
                ))}
            </select>
        </label>
    );
}

/**
 * The rate a project row was converted at: 1.0000 in the anchor currency,
 * a lock marker carrying its reason, or a "No rate" flag for a project left in
 * its own currency.
 */
export function FxRate({ fx }) {
    if (!fx) {
        return null;
    }

    if (fx.missing) {
        return (
            <Badge variant="warning" size="md">
                {$t('No rate for {{currency}}', { currency: fx.base_currency || '—' })}
            </Badge>
        );
    }

    if (fx.locked) {
        return (
            <span title={fx.lock_reason || ''}>
                <Badge variant="info" size="md" icon={<LockIcon />}>
                    {formatRate(fx.rate)}
                </Badge>
            </span>
        );
    }

    return <span className={classNames({ 'tw-text-charcoal-50': Number(fx.rate) === 1 })}>{formatRate(fx.rate)}</span>;
}

/** The note under a project left in its own currency. */
export function FxBaseCurrencyNote({ fx }) {
    if (!fx?.missing) {
        return null;
    }

    return <span className="tw-text-xs tw-text-charcoal-50">{$t('shown in base currency')}</span>;
}

/**
 * A total's label with its incomplete flag beside it when it has one. With
 * nothing to flag the label renders as it always did.
 */
export function withIncomplete(label, fx) {
    if (!fx?.incomplete) {
        return label;
    }

    return (
        <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-2">
            {label}
            <FxIncompleteFlag fx={fx} />
        </span>
    );
}

/**
 * Whether a period row holds a figure: its total or any period cell, so a
 * project whose periods net to zero still counts.
 */
function rowHasFigures(row) {
    const values = [row.total, ...Object.values(row.columns || {}).map((cell) => cell?.total)];

    return values.some((value) => Math.abs(Number(value) || 0) >= 0.005);
}

/**
 * Whether one section's total leaves projects out: the no-rate projects whose
 * row in the section holds something in the range. The export decides each
 * section's total the same way.
 */
export function sectionIncomplete(rows) {
    const missing = [
        ...new Set(
            (rows || [])
                .filter((row) => row.fx?.missing && row.has_activity !== false && rowHasFigures(row))
                .map((row) => row.fx.base_currency || $t('No currency')),
        ),
    ].sort();

    return missing.length > 0 ? { incomplete: true, missing_currencies: missing } : null;
}

/** The flag beside a total that leaves projects out. */
export function FxIncompleteFlag({ fx }) {
    if (!fx?.incomplete) {
        return null;
    }

    return (
        <Badge variant="danger" size="md">
            {$t('Incomplete: no rate for {{currencies}}', { currencies: fx.missing_currencies.join(', ') })}
        </Badge>
    );
}

/**
 * The note under a report table: how a chosen currency was reached, or that
 * each project is in its own.
 */
export function FxFootnote({ fx }) {
    return (
        <p className="tw-text-xs tw-text-charcoal-50 tw-mb-0">
            {fx
                ? $t(
                      "The Rate column shows each project's rate against the {{anchor}} anchor, common or locked; the lock marker shows a locked rate. Figures are converted to {{target}} through the anchor when the report is drawn. Nothing converted is stored.",
                      { anchor: fx.anchor, target: fx.show_in },
                  )
                : $t('Each project is shown in its own base currency.')}
        </p>
    );
}

/** How a converted total was reached, for the Basis tile. */
export function fxBasisText(fx) {
    const basis =
        fx.show_in === fx.anchor
            ? $t('Indicative consolidation at the rates on the FX page')
            : $t('Indicative consolidation via {{anchor}} at 1 {{anchor}} = {{rate}} {{target}}', {
                  anchor: fx.anchor,
                  rate: formatRate(fx.target_rate),
                  target: fx.show_in,
              });

    return fx.incomplete ? `${basis}. ${$t('Total excludes projects with no rate')}` : basis;
}

/** The warning above a converted report that leaves projects out of its totals. */
export function FxMissingAlert({ fx, fxUrl }) {
    if (!fx?.incomplete) {
        return null;
    }

    const currencies = fx.missing_currencies.join(', ');

    return (
        <Alert type="warning">
            <strong>{$t('No rate for {{currencies}}.', { currencies })}</strong>{' '}
            {$t('Those projects are still shown in their own currency and the total is incomplete.')}{' '}
            {fxUrl ? (
                <a href={fxUrl} className="tw-font-semibold tw-text-blue tw-underline">
                    {$t('Add the rate on the FX page')}
                </a>
            ) : (
                $t('Add the rate on the FX page')
            )}
            .
        </Alert>
    );
}
